import {useCurrentRepository} from '@github-ui/current-repository'
import {
  createConnectionMutationId,
  dispatchConnectionMutation,
  publishOptimisticFields,
  useEntityFieldsBridge,
} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {repositoryPath} from '@github-ui/paths'
import {useQuery} from '@github-ui/react-query'
import {ReactionViewerRelayServerQuery} from '@github-ui/server-persisted-queries'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import type {IssueViewerViewQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerViewQuery'
import type {ReactionViewerRelayServerQueryQuery} from '@github-ui/server-persisted-queries/types/ReactionViewerRelayServerQuery'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {ReactionViewerLoading} from './components/ReactionViewerLoading'
import {VALUES} from './constants/values'
import {useAddReactionMutation} from './mutations/add-reaction-mutation'
import {useRemoveReactionMutation} from './mutations/remove-reaction-mutation'
import {ReactionViewerBase} from './ReactionViewerBase'
import type {Reaction, ReactionContent, ReactionViewerGroup, Reactor} from './utils/ReactionGroups'

type ReactableIssue = NonNullable<NonNullable<IssueViewerViewQueryQuery['repository']>['issue']>
type ReactionGroupNode = NonNullable<NonNullable<ReactableIssue['reactionGroups']>[number]>

type ReactorNodeData = {
  readonly __typename: string
  readonly login?: string
  readonly avatarUrl?: string
  readonly name?: string | null
  readonly isCopilot?: boolean
  readonly isAgent?: boolean
  readonly displayName?: string
}

// Relay generates enums and abstract-type discriminators as "open": its reader types carry extra
// members for values the client doesn't know about, which the server-persisted-queries types omit.
// Accept those here so Relay data is assignable directly and callers need no cast; the mapper below
// narrows them back at runtime. `(string & {})` keeps the known members visible to autocomplete while
// still admitting a value added to the enum server-side.
type ReactionGroupData = Omit<ReactionGroupNode, 'content' | 'reactors'> & {
  readonly content: ReactionGroupNode['content'] | (string & {})
  readonly reactors: Omit<ReactionGroupNode['reactors'], 'nodes'> & {
    readonly nodes: ReadonlyArray<ReactorNodeData | null | undefined> | null
  }
}

// RELAY-CLEANUP(IssueBodyViewerReactable): widens nullable fields so data read by this
// still-Relay document stays assignable. Remove once it has left Relay.
export type ReactionViewerGroupsFragment = RelayCompat<{
  // Optional because callers may select this inside an `... on Reactable` inline fragment, where
  // Relay makes the field optional for nodes that aren't Reactable.
  readonly reactionGroups?: ReadonlyArray<ReactionGroupData | null> | null
}>

export type ReactionViewerProps = {
  reactionGroups: ReactionViewerGroupsFragment | null
  subjectId: string
  canReact?: boolean
  showReactors?: boolean
  bridgeReactionMutations?: boolean
  optimisticConnection?: {ownerId: string; connectionPath: string[]}
}

function getReactorTypeName(typeName: string): Reactor['typeName'] {
  switch (typeName) {
    case 'Bot':
    case 'Mannequin':
    case 'Organization':
    case 'User':
      return typeName
    default:
      return 'Other'
  }
}

function beginReactConnectionOverlay(
  connection: {ownerId: string; connectionPath: string[]},
  subjectId: string,
  optimisticFields: Record<string, unknown>,
) {
  const mutationId = createConnectionMutationId()
  const dispatch = (phase: 'optimistic' | 'completed', fields: Record<string, unknown>) =>
    dispatchConnectionMutation({
      phase,
      mutationId,
      ownerId: connection.ownerId,
      connectionPath: connection.connectionPath,
      operation: {type: 'setFields', id: subjectId, fields},
    })

  dispatch('optimistic', optimisticFields)

  return {
    completed: (confirmedFields: Record<string, unknown>) => dispatch('completed', confirmedFields),
    failed: (error: Error) => dispatchConnectionMutation({phase: 'error', mutationId, error}),
  }
}

export function ReactionViewer({
  reactionGroups: reactionGroupsData,
  subjectId,
  canReact = true,
  showReactors = false,
  bridgeReactionMutations = false,
  optimisticConnection,
}: ReactionViewerProps) {
  const environment = useRelayEnvironment()
  const {mutate: addReaction} = useAddReactionMutation(environment)
  const {mutate: removeReaction} = useRemoveReactionMutation(environment)

  const onReact = useCallback(
    (reaction: ReactionContent, viewerHasReacted: boolean) => {
      if (!canReact) return
      const optimisticReactionGroups = reactionGroupsData?.reactionGroups?.map(reactionGroup => {
        if (!reactionGroup || reactionGroup.content !== reaction) return reactionGroup

        return {
          ...reactionGroup,
          viewerHasReacted: !viewerHasReacted,
          reactors: {
            ...reactionGroup.reactors,
            totalCount: Math.max(0, reactionGroup.reactors.totalCount + (viewerHasReacted ? -1 : 1)),
          },
        }
      })
      const publication = optimisticConnection
        ? beginReactConnectionOverlay(optimisticConnection, subjectId, {reactionGroups: optimisticReactionGroups})
        : bridgeReactionMutations
          ? publishOptimisticFields(subjectId, {reactionGroups: optimisticReactionGroups})
          : undefined
      const failPublication = (error: Error) => publication?.failed(error)

      if (viewerHasReacted) {
        removeReaction({
          input: {subjectId, content: reaction, clientMutationId: null},
          onError: failPublication,
          onStale: () => failPublication(new Error('Reaction mutation superseded')),
          onCompleted: subject => {
            const reactionGroups = subject.reactionGroups
            if (reactionGroups) publication?.completed({reactionGroups})
            else failPublication(new Error('Failed to remove reaction'))
          },
        })
      } else {
        addReaction({
          input: {subjectId, content: reaction, clientMutationId: null},
          onError: failPublication,
          onStale: () => failPublication(new Error('Reaction mutation superseded')),
          onCompleted: subject => {
            const reactionGroups = subject.reactionGroups
            if (reactionGroups) publication?.completed({reactionGroups})
            else failPublication(new Error('Failed to add reaction'))
          },
        })
      }
    },
    [
      addReaction,
      bridgeReactionMutations,
      canReact,
      optimisticConnection,
      reactionGroupsData?.reactionGroups,
      removeReaction,
      subjectId,
    ],
  )

  // filtering out null reaction groups which can be expected when there's an error on the server
  const reactionViewerGroups = (reactionGroupsData?.reactionGroups || [])
    .filter(reactionGroup => reactionGroup != null)
    .map((reactionGroup): ReactionViewerGroup => {
      const reaction: Reaction = {
        // Narrow away Relay's open-enum member; unknown reaction types are not renderable.
        content: reactionGroup.content as ReactionContent,
        viewerHasReacted: reactionGroup.viewerHasReacted,
      }

      const reactors: Reactor[] =
        reactionGroup.reactors?.nodes?.map(reactor => {
          const typeName = getReactorTypeName(reactor?.__typename || '')
          if (!reactor?.login || typeName === 'Other') {
            return {login: '', typeName: 'Other'}
          }
          let displayName = reactor.name || undefined
          let tooltipName = reactor.login
          if (reactor.__typename === 'Bot') {
            if (reactor.isCopilot) {
              displayName = VALUES.copilotDisplayName
              tooltipName = VALUES.copilotDisplayName
            } else if (reactor.isAgent && reactor.displayName) {
              displayName = reactor.displayName
              tooltipName = reactor.displayName
            }
          }
          return {
            login: reactor.login,
            avatarUrl: reactor.avatarUrl,
            displayName,
            tooltipName,
            typeName,
          }
        }) || []

      return {
        reaction,
        reactors,
        totalCount: reactionGroup.reactors?.totalCount || 0,
      }
    })

  return (
    <ReactionViewerBase
      reactionGroups={reactionViewerGroups}
      canReact={canReact}
      showReactors={showReactors}
      onReact={onReact}
    />
  )
}

export function ReactionViewerRelayQueryComponent({id, subjectLocked}: {id: string; subjectLocked?: boolean}) {
  const {ownerLogin, name} = useCurrentRepository()
  const queryKey = ['reaction-viewer', {owner: ownerLogin, repo: name, id}] as const
  useEntityFieldsBridge()
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema -- Reactions are viewer-specific mutable state that must refetch, and this persisted GraphQL endpoint has no runtime schema.
  const {data, error, isPending} = useQuery({
    queryKey,
    queryFn: async ({signal}) => {
      const params = new URLSearchParams({queryId: ReactionViewerRelayServerQuery})
      const basePath = repositoryPath({owner: ownerLogin, repo: name})
      const response = await reactFetchJSON(`${basePath}/reaction_viewer/${encodeURIComponent(id)}?${params}`, {signal})

      if (!response.ok) throw new Error(`Reaction viewer fetch failed (${response.status})`)

      const json = (await response.json()) as {data?: ReactionViewerRelayServerQueryQuery; errors?: unknown[]}
      if (!json.data) throw new Error('Reaction viewer query returned no data')

      return json.data
    },
  })
  const mergedData = useOptimisticData(data, {targets: [{queryKey, revalidate: false}]})

  if (isPending) return <ReactionViewerLoading />
  if (error) throw error
  if (!mergedData) return null

  const node = mergedData.node
  if (!node || !('reactionGroups' in node)) return null

  return <ReactionViewer subjectId={id} reactionGroups={node} canReact={!subjectLocked} bridgeReactionMutations />
}

export default ReactionViewer
